<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div
      v-if="item.type !== 'notification'"
      class="ui-toast relative sm:w-97 my-5 bg-primary-white rounded-xl border component"
      :class="toastState.class"
    >
      <div
        :class="[!item.description ? 'items-center sm:py-3 sm:px-4 py-2 px-3' : 'sm:p-4 p-3']"
        class="flex flex-row justify-between sm:gap-5 gap-3"
      >
        <div
          :class="[{ 'items-center': !item.description }]"
          class="ui-toast__container grid gap-2.5"
        >
          <div
            class="h-10 w-10 flex items-center justify-center rounded-full"
            :style="{
              backgroundColor: toastState.icon.backgroundColor,
              border: `6px solid ${toastState.icon.borderColor}`,
            }"
          >
            <icon
              class="icon d-f ai-c lrt:mr-10 rtl:ml-10"
              :icon="toastState.icon.name"
              :color="toastState.icon.color"
              height="16.67"
              width="16.67"
            />
          </div>
          <div class="toast d-f ac-c flex flex-col">
            <div class="font-medium text-text-color sm:text-sm text-xs">
              <span v-if="item.type === 'error'">
                {{
                  $filters.capitalizeCharacterFromSentense(
                    item?.message || GENERAL_CONSTANT.EMPTY_RECORD_STRING,
                  )
                }}.
              </span>
              <span v-else>
                {{ addBreaksToLongWords(item?.message || GENERAL_CONSTANT.EMPTY_RECORD_STRING) }}
              </span>
              <a v-if="item.type === 'info' && item.learnMore" class="text-twitter-color underline">
                {{ GENERAL_CONSTANT.LEARN_MORE }}
              </a>
            </div>
            <div v-if="item.description" class="toast__body text-xs text-menu">
              {{ addBreaksToLongWords(item.description) }}
            </div>
          </div>
        </div>
        <div class="cursor-pointer ui-toast__actions">
          <span class="lh-0" @click="remove">
            <icon icon="cancel" height="20" width="20" color="text-color" />
          </span>
        </div>
      </div>
    </div>
    <div
      v-else
      class="ui-toast relative w-80 sm:w-97 my-5 rounded-xl bg-primary-white component border p-4"
    >
      <div :class="toastState.class" class="flex gap-6 justify-between">
        <div class="flex gap-2.5">
          <div
            class="min-h-[40px] min-w-[40px] h-10 w-10 flex items-center justify-center rounded-full"
            :style="{
              backgroundColor: toastState.icon.backgroundColor,
              border: `6px solid ${toastState.icon.borderColor}`,
            }"
          >
            <icon
              class="icon d-f ai-c lrt:mr-10 rtl:ml-10"
              :icon="toastState.icon.name"
              :color="toastState.icon.color"
              height="16.67"
              width="16.67"
            />
          </div>
          <div class="min-w-0">
            <div
              class="font-inter font-medium text-primary-gray-800 sm:text-sm text-xs break-words"
            >
              {{ $filters.capitalizeCharacterFromSentense(item.message) }}
            </div>
            <div
              v-if="isMarkdown(item.description)"
              class="mt-1 font-normal sm:text-sm text-xs text-left markdown-notification"
              v-html="renderMarkdown(item.description)"
            ></div>
            <div
              v-else
              class="mt-1 font-inter font-normal sm:text-sm text-xs leading-5 text-primary-gray-600 text-left"
            >
              {{ item.description }}
            </div>
            <div
              class="mt-3 font-inter flex"
              :class="item.learnMore ? 'justify-between' : 'justify-end'"
            >
              <div v-if="item.learnMore" class="text-sm font-medium text-primary-green">
                {{ GENERAL_CONSTANT.LEARN_MORE }}
              </div>
              <div class="text-xs text-menu justify-end-end">
                {{ $filters.getTimeOnly(item.inserted_at, DATE_TIME_CONSTANT.time12H) }}
              </div>
            </div>
          </div>
        </div>
        <div class="cursor-pointer ui-toast__actions">
          <span class="lh-0" @click="remove">
            <icon icon="cancel" height="20" width="20" color="primary-gray-400" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import icon from '@components/icons/icon.vue'
import GENERAL_CONSTANT from '@src/constants/general-constants'
import DATE_TIME_CONSTANT from '@src/constants/date-time-constants.js'
import { renderMarkdown, isMarkdown } from '@src/lib/markdown'
import { addBreaksToLongWords } from '@/src/utils/generalUtil'
export default {
  name: 'UiToast',
  components: {
    icon,
  },
  props: {
    item: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  emits: ['remove'],
  data() {
    return {
      GENERAL_CONSTANT,
      DATE_TIME_CONSTANT,
    }
  },

  computed: {
    /*
     * Checks the position of the Ui-toast.
     * @param {void} Nothing.
     * @retuns {null/true} return value is based on the Boolean value of the "this.item.description" data property
     */
    isCentered() {
      return this.item.description ? null : true
    },

    /*
     * Sets the current state of the toast based on the value of the "this.item.type" data property.
     * @param {void} Nothing.
     * @retuns {object} state - the current state of the toast.
     */
    toastState() {
      let state = {
        class: 'default',
        icon: {
          name: 'checkCircle',
          color: 'menu',
          backgroundColor: 'white',
          borderColor: 'black',
        },
      }

      switch (this.item.type) {
        case 'success':
          state = {
            class: 'success',
            icon: {
              name: 'check-circle',
              color: 'primary-green',
              backgroundColor: 'var(--primary-light-green-100)',
              borderColor: 'var(--primary-light-green-50)',
            },
          }
          break
        case 'warning':
          state = {
            class: 'warning',
            icon: {
              name: 'exclamationCircle',
              color: 'text-color-info',
            },
          }
          break
        case 'error':
          state = {
            class: 'error',
            icon: {
              name: 'exclamationCircle',
              color: 'text-color-danger',
              backgroundColor: 'var(--error-100)',
              borderColor: 'var(--error-50)',
            },
          }
          break
        case 'notification':
          state = {
            class: 'notification',
            icon: {
              name: 'check-circle',
              color: 'primary-green',
              backgroundColor: 'var(--primary-light-green-100)',
              borderColor: 'var(--primary-light-green-50)',
            },
          }
          break
        case 'info':
          state = {
            class: 'info',
            icon: {
              name: 'exclamationCircle',
              color: 'primary-purple-600',
              backgroundColor: 'var(--primary-purple-100)',
              borderColor: 'var(--primary-purple-50)',
            },
          }
          break
        default:
          state = {
            class: 'default',
            icon: {
              name: 'exclamationCircle',
              color: 'menu',
            },
          }
          break
      }

      return state
    },
  },

  mounted() {
    if (this.item.duration) {
      const timer = setTimeout(() => {
        clearTimeout(timer)
        if (!this.item.sticky) this.remove()
      }, this.item.duration)
    }
  },

  methods: {
    addBreaksToLongWords,
    isMarkdown,
    renderMarkdown,
    remove() {
      this.$emit('remove', this.item.id)
    },
  },
}
</script>

<style lang="scss">
.ui-toast {
  box-shadow: 2px 6px 19px #00000029;
  .toast {
    &__body {
      margin-top: 4px;

      /* @include font('Roboto', 0.8125em, normal); */

      /* color: $label-text; */
      color: var(--label-text);
    }
  }
  &.default {
    border: 1px solid;
  }
  &__container {
    grid-template-columns: auto 1fr;
    width: 89%;
  }
  &__actions {
    height: fit-content;
  }
  &__action {
    position: absolute;
    top: 18px;
    right: 10px;
    span {
      cursor: pointer;
    }
    &--centered {
      top: 24px;
      transform: translateY(10px);
    }
  }
}

@media (max-width: 475px) {
  .ui-toast {
    width: 320px;
  }
}

@media (max-width: 400px) {
  .ui-toast {
    width: 280px;
  }
}
</style>
